import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import OperatorLayout from "./OperatorLayout";

interface MatchingWeights {
  stack_match: number;
  urgency_fit: number;
  geo_fit: number;
  reliability: number;
  quality: number;
}

interface MatchingLimits {
  max_providers_per_request: number;
  min_score_threshold: number;
  fairness_delta: number;
  expiry_hours: number;
  overage_percent: number;
}

interface AISettings {
  enabled: boolean;
  model: string;
  enhance_reasons: boolean;
  adjust_scores: boolean;
  max_adjustment: number;
}

const DEFAULT_WEIGHTS: MatchingWeights = { stack_match: 35, urgency_fit: 25, geo_fit: 10, reliability: 15, quality: 15 };
const DEFAULT_LIMITS: MatchingLimits = { max_providers_per_request: 3, min_score_threshold: 20, fairness_delta: 5, expiry_hours: 48, overage_percent: 20 };
const DEFAULT_AI: AISettings = { enabled: false, model: "google/gemini-3-flash-preview", enhance_reasons: true, adjust_scores: true, max_adjustment: 10 };

const MODEL_OPTIONS = [
  { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash (Schnell)" },
  { id: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash (Balanced)" },
  { id: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro (Präzise)" },
];

const OperatorMatchingSettings = () => {
  const { t } = useTranslation();
  const [weights, setWeights] = useState<MatchingWeights>(DEFAULT_WEIGHTS);
  const [limits, setLimits] = useState<MatchingLimits>(DEFAULT_LIMITS);
  const [aiSettings, setAiSettings] = useState<AISettings>(DEFAULT_AI);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<"weights" | "limits" | "ai">("weights");

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.from("matching_config").select("config_key, config_value");
    if (!error && data) {
      const configMap = data.reduce<Record<string, any>>((acc, row) => { acc[row.config_key] = row.config_value; return acc; }, {});
      if (configMap.weights) setWeights(configMap.weights as MatchingWeights);
      if (configMap.limits) setLimits(configMap.limits as MatchingLimits);
      if (configMap.ai_settings) setAiSettings(configMap.ai_settings as AISettings);
    }
    setIsLoading(false);
  };

  const totalWeight = weights.stack_match + weights.urgency_fit + weights.geo_fit + weights.reliability + weights.quality;

  const handleSave = async () => {
    if (totalWeight !== 100) {
      toast.error(t("op.matchWeightError", { total: totalWeight }));
      return;
    }
    setIsSaving(true);
    try {
      const updates = [
        { config_key: "weights", config_value: weights },
        { config_key: "limits", config_value: limits },
        { config_key: "ai_settings", config_value: aiSettings },
      ];
      for (const u of updates) {
        const { data: existing } = await supabase.from("matching_config").select("id").eq("config_key", u.config_key).maybeSingle();
        if (existing) {
          const { error } = await supabase.from("matching_config").update({ config_value: u.config_value as any }).eq("config_key", u.config_key);
          if (error) throw error;
        } else {
          const { error } = await supabase.from("matching_config").insert({ config_key: u.config_key, config_value: u.config_value as any });
          if (error) throw error;
        }
      }
      toast.success(t("op.matchSaved"));
    } catch (err: any) {
      toast.error(t("common.error") + ": " + (err.message || ""));
    } finally {
      setIsSaving(false);
    }
  };

  const sections = [
    { id: "weights" as const, label: t("op.matchWeights") },
    { id: "limits" as const, label: t("op.matchLimits") },
    { id: "ai" as const, label: t("op.matchAI") },
  ];

  const weightFields: { key: keyof MatchingWeights; label: string; description: string }[] = [
    { key: "stack_match", label: t("op.matchStackMatch"), description: t("op.matchStackMatchDesc") },
    { key: "urgency_fit", label: t("op.matchUrgencyFit"), description: t("op.matchUrgencyFitDesc") },
    { key: "geo_fit", label: t("op.matchGeoFit"), description: t("op.matchGeoFitDesc") },
    { key: "reliability", label: t("op.matchReliability"), description: t("op.matchReliabilityDesc") },
    { key: "quality", label: t("op.matchQuality"), description: t("op.matchQualityDesc") },
  ];

  if (isLoading) {
    return (
      <OperatorLayout title={t("op.matchTitle")} subtitle={t("op.matchSubtitle")}>
        <p className="text-caption">{t("common.loading")}</p>
      </OperatorLayout>
    );
  }

  return (
    <OperatorLayout title={t("op.matchTitle")} subtitle={t("op.matchSubtitle")}>
      <div className="flex gap-8">
        <div className="w-48 shrink-0 space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-l-2 ${
                activeSection === section.id
                  ? "border-accent text-foreground bg-accent/5"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div className="flex-1 max-w-2xl">
          {activeSection === "weights" && (
            <div className="space-y-6">
              <div className="p-6 border border-border bg-card space-y-6">
                <div>
                  <h3 className="text-subheadline mb-1">{t("op.matchScoreWeights")}</h3>
                  <p className="text-caption">
                    {t("op.matchScoreDesc")}{" "}
                    {t("op.matchCurrent")} <span className={totalWeight === 100 ? "text-accent" : "text-destructive"}>{totalWeight}/100</span>
                  </p>
                </div>

                {weightFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-body font-medium">{field.label}</label>
                        <p className="text-micro mt-0.5">{field.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <input type="range" min={0} max={50} value={weights[field.key]}
                          onChange={(e) => setWeights({ ...weights, [field.key]: Number(e.target.value) })}
                          className="w-32 accent-[hsl(var(--accent))]" />
                        <input type="number" min={0} max={50} value={weights[field.key]}
                          onChange={(e) => setWeights({ ...weights, [field.key]: Number(e.target.value) })}
                          className="w-16 h-9 px-2 text-center border border-border bg-background text-body focus:outline-none focus:border-primary" />
                      </div>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-accent/60 rounded-full transition-all duration-300" style={{ width: `${weights[field.key] * 2}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="commit" onClick={handleSave} disabled={isSaving || totalWeight !== 100}>
                {isSaving ? t("op.matchSaving") : t("op.matchSaveConfig")}
              </Button>
            </div>
          )}

          {activeSection === "limits" && (
            <div className="space-y-6">
              <div className="p-6 border border-border bg-card space-y-6">
                <div>
                  <h3 className="text-subheadline mb-1">{t("op.matchLimitsTitle")}</h3>
                  <p className="text-caption">{t("op.matchLimitsDesc")}</p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-micro block">{t("op.matchMaxProviders")}</label>
                    <input type="number" min={1} max={10} value={limits.max_providers_per_request}
                      onChange={(e) => setLimits({ ...limits, max_providers_per_request: Number(e.target.value) })}
                      className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                    <p className="text-micro">{t("op.matchMaxProvidersDesc")}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("op.matchMinScore")}</label>
                    <input type="number" min={0} max={100} value={limits.min_score_threshold}
                      onChange={(e) => setLimits({ ...limits, min_score_threshold: Number(e.target.value) })}
                      className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                    <p className="text-micro">{t("op.matchMinScoreDesc")}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("op.matchFairnessDelta")}</label>
                    <input type="number" min={1} max={20} value={limits.fairness_delta}
                      onChange={(e) => setLimits({ ...limits, fairness_delta: Number(e.target.value) })}
                      className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                    <p className="text-micro">{t("op.matchFairnessDeltaDesc")}</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("op.matchExpiryHours")}</label>
                    <input type="number" min={1} max={168} value={limits.expiry_hours}
                      onChange={(e) => setLimits({ ...limits, expiry_hours: Number(e.target.value) })}
                      className="w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                    <p className="text-micro">{t("op.matchExpiryDesc")}</p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <label className="text-micro block">{t("op.matchOverage")}</label>
                    <div className="flex items-center gap-4">
                      <input type="number" min={0} max={100} value={limits.overage_percent}
                        onChange={(e) => setLimits({ ...limits, overage_percent: Number(e.target.value) })}
                        className="w-24 h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                      <span className="text-caption">{t("op.matchOverageDesc", { percent: limits.overage_percent })}</span>
                    </div>
                    <p className="text-micro">{t("op.matchOverageExample")}</p>
                  </div>
                </div>
              </div>
              <Button variant="commit" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t("op.matchSaving") : t("op.matchSaveConfig")}
              </Button>
            </div>
          )}

          {activeSection === "ai" && (
            <div className="space-y-6">
              <div className="p-6 border border-border bg-card space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-subheadline mb-1">{t("op.matchAITitle")}</h3>
                    <p className="text-caption">{t("op.matchAIDesc")}</p>
                  </div>
                  <button
                    onClick={() => setAiSettings({ ...aiSettings, enabled: !aiSettings.enabled })}
                    className={`relative w-12 h-6 rounded-full transition-colors ${aiSettings.enabled ? "bg-accent" : "bg-muted"}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-card shadow transition-transform ${aiSettings.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                  </button>
                </div>

                {aiSettings.enabled && (
                  <>
                    <div className="divider-line" />
                    <div className="space-y-2">
                      <label className="text-micro block">{t("op.matchAIModel")}</label>
                      <div className="space-y-2">
                        {MODEL_OPTIONS.map((m) => (
                          <button key={m.id} onClick={() => setAiSettings({ ...aiSettings, model: m.id })}
                            className={`w-full text-left px-4 py-2.5 border text-sm transition-all ${
                              aiSettings.model === m.id ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:border-muted-foreground"
                            }`}>
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="divider-line" />
                    <div className="space-y-4">
                      <span className="text-micro block">{t("op.matchAIFunctions")}</span>
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <p className="text-body">{t("op.matchAIEnhanceReasons")}</p>
                          <p className="text-micro mt-0.5">{t("op.matchAIEnhanceReasonsDesc")}</p>
                        </div>
                        <input type="checkbox" checked={aiSettings.enhance_reasons}
                          onChange={(e) => setAiSettings({ ...aiSettings, enhance_reasons: e.target.checked })}
                          className="w-4 h-4 accent-[hsl(var(--accent))]" />
                      </label>
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <p className="text-body">{t("op.matchAIAdjustScores")}</p>
                          <p className="text-micro mt-0.5">{t("op.matchAIAdjustScoresDesc")}</p>
                        </div>
                        <input type="checkbox" checked={aiSettings.adjust_scores}
                          onChange={(e) => setAiSettings({ ...aiSettings, adjust_scores: e.target.checked })}
                          className="w-4 h-4 accent-[hsl(var(--accent))]" />
                      </label>
                      {aiSettings.adjust_scores && (
                        <div className="space-y-2 pl-4 border-l-2 border-accent/20">
                          <label className="text-micro block">{t("op.matchAIMaxAdjustment")}</label>
                          <input type="number" min={1} max={25} value={aiSettings.max_adjustment}
                            onChange={(e) => setAiSettings({ ...aiSettings, max_adjustment: Number(e.target.value) })}
                            className="w-24 h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary" />
                          <p className="text-micro">{t("op.matchAIMaxAdjustmentDesc")}</p>
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-secondary/30 border border-border">
                      <span className="text-micro block mb-2">{t("op.matchAIHowTitle")}</span>
                      <ol className="text-caption space-y-1 list-decimal list-inside">
                        <li>{t("op.matchScoreDesc")}</li>
                        <li>{t("op.matchAIDesc")}</li>
                        <li>{t("op.matchAIEnhanceReasonsDesc")}</li>
                        <li>{t("op.matchAIMaxAdjustmentDesc")} (±{aiSettings.max_adjustment})</li>
                      </ol>
                    </div>
                  </>
                )}
              </div>
              <Button variant="commit" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t("op.matchSaving") : t("op.matchSaveConfig")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </OperatorLayout>
  );
};

export default OperatorMatchingSettings;
